import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import {
  useTrips,
  type BusinessTrip,
  type TripUser,
  type TripSortField,
  type TripListOptions,
} from '@/hooks/trips/useTrips';
import { useApproveTrip } from '@/hooks/trips/useApproveTrip';
import { useDeclineTrip } from '@/hooks/trips/useDeclineTrip';
import { useCancelTrip } from '@/hooks/trips/useCancelTrip';
import { useDeleteTrip } from '@/hooks/trips/useDeleteTrip';
import { useExtendTrip, useShortenTrip } from '@/hooks/trips/useExtendTrip';
import ActionDropdown, { type DropdownAction } from '@/components/ui/ActionDropdown';
import { useState, useMemo, memo, useRef, useEffect } from 'react';
import { useTranslations } from '@/i18n';
import { useRouter } from '@/lib/navigation';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import TripExtendDialog from '@/components/trips/TripExtendDialog';
import TripShortenDialog from '@/components/trips/TripShortenDialog';
import { useToast } from '@/components/ui/toast';
import { canShowApprovalActions } from '@/config/approval-actions';
import { format } from 'date-fns';

import TripsListMobile from '@/components/trips/table/TripsListMobile';
import type { TripListViewState } from '@/components/trips/table/types';
import {
  DATE_PLACEHOLDER,
  formatDateRange,
  formatSubmittedDate,
  getStatusBadge,
} from '@/components/trips/table/trip-list-utils';
import { TripListPagination } from '@/components/trips/table/TripListPagination';
import type { Translator } from '@/i18n/useTranslations';

const DEFAULT_PAGE = 1;
const PAGE_SIZE = 15;
const MAX_PAGE_BUTTONS = 5;
const SORTING_ENABLED = false;

type FiltersMap = Record<string, string[]>;

const EMPTY_FILTERS: FiltersMap = {};

function normalizeFilters(filters: FiltersMap): FiltersMap {
  const entries = Object.keys(filters)
    .filter((key) => Array.isArray(filters[key]) && filters[key]!.length > 0)
    .sort()
    .map((key) => [key, [...filters[key]!.sort()]] as [string, string[]]);

  return Object.fromEntries(entries);
}

function getPageNumbers(current: number, total: number, maxButtons: number) {
  if (total <= maxButtons) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(total, start + maxButtons - 1);

  if (end - start + 1 < maxButtons) {
    start = Math.max(1, end - maxButtons + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

type TableColumnId =
  | 'initiator'
  | 'author'
  | 'number'
  | 'duration'
  | 'submittedAt'
  | 'myStatus'
  | 'status';

interface TableColumnDefinition {
  id: TableColumnId;
  labelKey: string;
  widthClass: string;
  sortField?: TripSortField;
}

const BASE_TABLE_COLUMNS: TableColumnDefinition[] = [
  {
    id: 'initiator',
    labelKey: 'tableHeaders.initiator',
    widthClass: 'w-[18%]',
  },
  {
    id: 'author',
    labelKey: 'tableHeaders.author',
    widthClass: 'w-[18%]',
  },
  {
    id: 'number',
    labelKey: 'tableHeaders.number',
    widthClass: 'w-[14%]',
    sortField: 'name' as TripSortField,
  },
  {
    id: 'duration',
    labelKey: 'tableHeaders.duration',
    widthClass: 'w-[20%]',
    sortField: 'start_date' as TripSortField,
  },
  {
    id: 'submittedAt',
    labelKey: 'tableHeaders.submittedAt',
    widthClass: 'w-[15%]',
  },
  {
    id: 'status',
    labelKey: 'tableHeaders.status',
    widthClass: 'w-[15%]',
    sortField: 'state' as TripSortField,
  },
];

const APPROVAL_TABLE_COLUMNS: TableColumnDefinition[] = [
  {
    id: 'initiator',
    labelKey: 'tableHeaders.initiator',
    widthClass: 'w-[17%]',
  },
  {
    id: 'author',
    labelKey: 'tableHeaders.author',
    widthClass: 'w-[17%]',
  },
  {
    id: 'number',
    labelKey: 'tableHeaders.number',
    widthClass: 'w-[13%]',
    sortField: 'name' as TripSortField,
  },
  {
    id: 'duration',
    labelKey: 'tableHeaders.duration',
    widthClass: 'w-[18%]',
    sortField: 'start_date' as TripSortField,
  },
  {
    id: 'submittedAt',
    labelKey: 'tableHeaders.submittedAt',
    widthClass: 'w-[13%]',
  },
  {
    id: 'myStatus',
    labelKey: 'tableHeaders.myStatus',
    widthClass: 'w-[11%]',
  },
  {
    id: 'status',
    labelKey: 'tableHeaders.status',
    widthClass: 'w-[11%]',
    sortField: 'state' as TripSortField,
  },
];

type ColumnClassMap = Partial<Record<TableColumnId, string>>;

type TripListQueryHook = (options: TripListOptions) => ReturnType<typeof useTrips>;

interface TripsTableProps {
  filters?: FiltersMap;
  search?: string;
  useTripsHook?: TripListQueryHook;
  variant?: 'default' | 'approvals';
  currentUserRoles?: readonly string[] | null;
}

function SortIndicator({ direction, active }: { direction: 'asc' | 'desc'; active: boolean }) {
  const rotation = direction === 'asc' ? 'rotate-180' : '';
  const stroke = active ? '#292929' : '#C5C5C5';

  return (
    <svg
      aria-hidden="true"
      className={`h-4 w-4 transition-transform ${rotation}`}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.5 9L12.5 15L18.5 9"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface DesktopTableViewProps {
  columns: TableColumnDefinition[];
  t: Translator;
  sortBy: TripSortField | undefined;
  sortOrder: 'asc' | 'desc';
  handleSortChange: (field?: TripSortField) => void;
  variant: 'default' | 'approvals';
  currentUserRoles?: readonly string[] | null;
  viewState: TripListViewState;
}

function DesktopTableView({
  columns,
  t,
  sortBy,
  sortOrder,
  handleSortChange,
  variant,
  currentUserRoles,
  viewState,
}: DesktopTableViewProps) {
  const { data, trips, isLoading, error } = viewState;

  if (isLoading && !data) {
    return (
      <div className="w-full bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#f0f0f0] hover:bg-transparent">
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={`h-[4.25rem] ${column.widthClass} text-left text-[0.9375rem] leading-[1.5rem] font-medium text-black`}
                >
                  {t(column.labelKey)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 8 }).map((_, index) => (
              <TableRow key={index} className="border-b border-[#f0f0f0]">
                {columns.map((column) => {
                  switch (column.id) {
                    case 'initiator':
                    case 'author':
                      return (
                        <TableCell
                          key={`${index}-${column.id}`}
                          className={`h-[4.25rem] ${column.widthClass} text-left`}
                        >
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-[2rem] w-[2rem] rounded-full" />
                            <Skeleton className="h-[1rem] w-[8rem]" />
                          </div>
                        </TableCell>
                      );
                    case 'number':
                    case 'duration':
                    case 'submittedAt':
                      return (
                        <TableCell
                          key={`${index}-${column.id}`}
                          className={`h-[4.25rem] ${column.widthClass} text-left`}
                        >
                          <Skeleton className="h-[1rem] w-[6rem]" />
                        </TableCell>
                      );
                    case 'myStatus':
                    case 'status':
                    default:
                      return (
                        <TableCell
                          key={`${index}-${column.id}`}
                          className={`h-[4.25rem] ${column.widthClass} text-left`}
                        >
                          <Skeleton className="h-[1.5rem] w-[5rem] rounded-full" />
                        </TableCell>
                      );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white">
        <div className="p-8 text-center">
          <div className="mb-2 text-red-500">{t('errorLoading')}</div>
          <div className="text-sm text-gray-600">{error.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-[#f0f0f0] hover:bg-transparent">
            {columns.map((column) => {
              const isSortable = SORTING_ENABLED && Boolean(column.sortField);
              const isActive = isSortable && sortBy === column.sortField;
              const direction = isActive ? sortOrder : 'asc';

              return (
                <TableHead
                  key={column.id}
                  className={`h-[4.25rem] ${column.widthClass} text-left text-[0.9375rem] leading-[1.5rem] font-medium text-black`}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => handleSortChange(column.sortField)}
                      className="flex items-center gap-2 text-left"
                    >
                      <span>{t(column.labelKey)}</span>
                      <SortIndicator direction={direction} active={isActive} />
                    </button>
                  ) : (
                    <span>{t(column.labelKey)}</span>
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {trips.length > 0 ? (
            trips.map((trip) => (
              <TripRow
                key={trip.id}
                trip={trip}
                t={t}
                columns={columns}
                variant={variant}
                currentUserRoles={currentUserRoles}
              />
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="py-12 text-center text-sm leading-5 text-[#666666]"
              >
                {t('empty')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TripListPagination t={t} viewState={viewState} />
    </div>
  );
}

const UserCell = memo(({ user }: { user: TripUser }) => (
  <div className="flex min-w-0 items-center gap-3">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2]">
      {user.avatar ? (
        <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-medium text-white">
          {user.name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .substring(0, 2)}
        </span>
      )}
    </div>
    <span className="min-w-0 truncate text-sm font-medium text-[#666]" title={user.name}>
      {user.name}
    </span>
  </div>
));

UserCell.displayName = 'UserCell';

const TripRow = memo(
  ({
    trip,
    t,
    columns,
    variant,
    currentUserRoles,
  }: {
    trip: BusinessTrip;
    t: (key: string) => string;
    columns: TableColumnDefinition[];
    variant: 'default' | 'approvals';
    currentUserRoles?: readonly string[] | null;
  }) => {
    const router = useRouter();
    const [isHovered, setIsHovered] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [showRejectConfirm, setShowRejectConfirm] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showExtendConfirm, setShowExtendConfirm] = useState(false);
    const [showShortenConfirm, setShowShortenConfirm] = useState(false);
    const dropdownClosingRef = useRef(false);
    const approveTripMutation = useApproveTrip();
    const declineTripMutation = useDeclineTrip();
    const cancelTripMutation = useCancelTrip();
    const deleteTripMutation = useDeleteTrip();
    const extendTripMutation = useExtendTrip();
    const shortenTripMutation = useShortenTrip();
    const { showToast } = useToast();
    const tToasts = useTranslations('toasts');
    const [activeApproveTripId, setActiveApproveTripId] = useState<string | null>(null);
    const [activeDeclineTripId, setActiveDeclineTripId] = useState<string | null>(null);
    const [activeCancelTripId, setActiveCancelTripId] = useState<string | null>(null);
    const [activeDeleteTripId, setActiveDeleteTripId] = useState<string | null>(null);
    const [activeExtendTripId, setActiveExtendTripId] = useState<string | null>(null);
    const [activeShortenTripId, setActiveShortenTripId] = useState<string | null>(null);
    const [extendErrorMessage, setExtendErrorMessage] = useState<string | null>(null);
    const [shortenErrorMessage, setShortenErrorMessage] = useState<string | null>(null);
    const columnClassMap = useMemo<ColumnClassMap>(() => {
      return columns.reduce<ColumnClassMap>((accumulator, column) => {
        accumulator[column.id] = column.widthClass;
        return accumulator;
      }, {});
    }, [columns]);
    const hasMyStatusColumn = useMemo(
      () => columns.some((column) => column.id === 'myStatus'),
      [columns]
    );
    const isCcEmployee = useMemo(() => {
      if (!currentUserRoles || currentUserRoles.length === 0) {
        return false;
      }

      return currentUserRoles
        .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
        .map((role) =>
          role
            .trim()
            .toUpperCase()
            .replace(/^ROLE_/, '')
        )
        .includes('CC_EMPLOYEE');
    }, [currentUserRoles]);
    const submittedDate = formatSubmittedDate(trip.submittedAt);
    const roamingApprovalLabel = useMemo(() => {
      if (!trip.roamingExpense) {
        return null;
      }
      return trip.roamingActivated
        ? t('statuses.approvedRoamingActive')
        : t('statuses.approvedRoamingPending');
    }, [t, trip.roamingActivated, trip.roamingExpense]);
    const statusLabelOverride =
      variant === 'default' && trip.status === 'approved' ? roamingApprovalLabel : null;
    const myStatusLabelOverride =
      variant === 'approvals' && trip.myState === 'approved' && isCcEmployee
        ? roamingApprovalLabel
        : null;
    const myStateBadge =
      trip.myState && trip.myState !== 'unknown'
        ? getStatusBadge(trip.myState, t, myStatusLabelOverride)
        : null;
    const isApprovingThisTrip = approveTripMutation.isPending && activeApproveTripId === trip.id;
    const isDecliningThisTrip = declineTripMutation.isPending && activeDeclineTripId === trip.id;
    const isCancellingThisTrip = cancelTripMutation.isPending && activeCancelTripId === trip.id;
    const isDeletingThisTrip = deleteTripMutation.isPending && activeDeleteTripId === trip.id;
    const isExtendingThisTrip = extendTripMutation.isPending && activeExtendTripId === trip.id;
    const isShorteningThisTrip = shortenTripMutation.isPending && activeShortenTripId === trip.id;
    const isActionPending =
      isApprovingThisTrip ||
      isDecliningThisTrip ||
      isCancellingThisTrip ||
      isDeletingThisTrip ||
      isExtendingThisTrip ||
      isShorteningThisTrip;
    // Use availableActions from API to determine what actions the user can perform
    const availableActions = trip.availableActions ?? [];
    const canApprove = availableActions.includes('approve');
    const canDecline = availableActions.includes('decline');
    const canCancel = availableActions.includes('cancel');
    const canExtend = availableActions.includes('extend');
    const canShorten = availableActions.includes('shorten');
    // For draft trips, the API returns 'cancel' but we should show 'delete' instead
    const isDraft = trip.status === 'draft';
    const canDelete = isDraft && canCancel;

    // Show approval controls on approvals page if user can approve or decline
    // Hide for CC_EMPLOYEE, FUEL_ADMIN, and CEO_ASSISTANT roles
    const shouldShowApprovalControls =
      variant === 'approvals' &&
      canShowApprovalActions(currentUserRoles) &&
      (canApprove || canDecline || isActionPending);

    // Show action menu on default variant if there are any available actions
    // For draft trips, canDelete replaces canCancel
    const shouldShowActionMenu =
      variant === 'default' && (canDelete || (!isDraft && canCancel) || canExtend || canShorten);

    const dropdownActions: DropdownAction[] = useMemo(() => {
      if (!shouldShowActionMenu) {
        return [];
      }

      const actions: DropdownAction[] = [];

      // For draft trips, show Delete instead of Cancel
      if (canDelete) {
        actions.push({
          id: 'delete',
          label: t('actions.delete'),
          icon: 'delete',
          variant: 'destructive',
          onClick: () => {
            setShowDeleteConfirm(true);
            setIsDropdownOpen(false);
            setIsHovered(false);
          },
        });
      } else if (canCancel) {
        // For non-draft trips, show Cancel
        actions.push({
          id: 'cancel',
          label: t('actions.cancel'),
          icon: 'cancel',
          variant: 'destructive',
          onClick: () => {
            setShowCancelConfirm(true);
            setIsDropdownOpen(false);
            setIsHovered(false);
          },
        });
      }

      if (canExtend) {
        actions.push({
          id: 'extend',
          label: t('actions.extend'),
          icon: 'extend',
          onClick: () => {
            setShowExtendConfirm(true);
            setIsDropdownOpen(false);
            setIsHovered(false);
          },
        });
      }

      if (canShorten) {
        actions.push({
          id: 'shorten',
          label: t('actions.shrink'),
          icon: 'shrink',
          onClick: () => {
            setShowShortenConfirm(true);
            setIsDropdownOpen(false);
            setIsHovered(false);
          },
        });
      }

      return actions;
    }, [shouldShowActionMenu, canDelete, canCancel, canExtend, canShorten, t]);
    const hasRowActions =
      shouldShowApprovalControls || (shouldShowActionMenu && dropdownActions.length > 0);

    const handlePointerEnter = () => setIsHovered(true);
    const handlePointerLeave = () => {
      // Only hide hover state if dropdown is not open
      if (!isDropdownOpen) {
        setIsHovered(false);
      }
    };

    const handleDropdownOpenChange = (open: boolean) => {
      setIsDropdownOpen(open);
      // If dropdown closes, set a flag to prevent row clicks briefly
      if (!open) {
        dropdownClosingRef.current = true;
        setIsHovered(false);
        // Reset the flag after a short delay
        setTimeout(() => {
          dropdownClosingRef.current = false;
        }, 200);
      }
    };

    const handleApprove = () => {
      setShowApproveConfirm(true);
    };

    const confirmApprove = () => {
      setActiveApproveTripId(trip.id);
      approveTripMutation.mutate(trip.id, {
        onSuccess: () => {
          showToast({ message: tToasts('tripApproved'), variant: 'success' });
        },
        onError: (error) => {
          console.error('Failed to approve trip:', error);
        },
        onSettled: () => {
          setShowApproveConfirm(false);
          setActiveApproveTripId(null);
        },
      });
    };

    const handleReject = () => {
      if (!canDecline) {
        return;
      }
      setShowRejectConfirm(true);
    };

    const confirmReject = async () => {
      if (!canDecline) {
        setShowRejectConfirm(false);
        return;
      }

      setActiveDeclineTripId(trip.id);
      try {
        await declineTripMutation.mutate({ tripId: trip.id, comment: '' });
        console.log('Trip rejected successfully');
      } catch (error) {
        console.error('Failed to reject trip:', error);
      } finally {
        setShowRejectConfirm(false);
        setActiveDeclineTripId(null);
      }
    };

    const confirmCancel = async () => {
      if (!shouldShowActionMenu) {
        setShowCancelConfirm(false);
        return;
      }

      setActiveCancelTripId(trip.id);
      try {
        await cancelTripMutation.mutateAsync(trip.id);
        console.log('Trip cancelled successfully');
      } catch (error) {
        console.error('Failed to cancel trip:', error);
      } finally {
        setShowCancelConfirm(false);
        setActiveCancelTripId(null);
        setIsDropdownOpen(false);
        setIsHovered(false);
      }
    };

    const confirmDelete = async () => {
      if (!canDelete) {
        setShowDeleteConfirm(false);
        return;
      }

      setActiveDeleteTripId(trip.id);
      try {
        await deleteTripMutation.mutateAsync(trip.id);
        console.log('Trip deleted successfully');
      } catch (error) {
        console.error('Failed to delete trip:', error);
      } finally {
        setShowDeleteConfirm(false);
        setActiveDeleteTripId(null);
        setIsDropdownOpen(false);
        setIsHovered(false);
      }
    };

    const confirmExtend = async (newEndDate: Date) => {
      if (!shouldShowActionMenu) {
        setShowExtendConfirm(false);
        setExtendErrorMessage(null);
        return;
      }

      setActiveExtendTripId(trip.id);
      setExtendErrorMessage(null);
      try {
        const formattedDate = format(newEndDate, 'yyyy-MM-dd');
        const newTrip = await extendTripMutation.mutateAsync({
          tripId: trip.id,
          endDate: formattedDate,
        });
        showToast({
          message: (tToasts('tripExtended') as string) ?? t('actions.extend'),
          variant: 'success',
        });
        if (newTrip?.id) {
          router.push(`/trips/${newTrip.id}/edit?derived=extend`);
        }
        setShowExtendConfirm(false);
        setActiveExtendTripId(null);
        setIsDropdownOpen(false);
        setIsHovered(false);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('errorLoading') || 'Failed to extend trip';
        setExtendErrorMessage(message);
        setActiveExtendTripId(null);
      }
    };

    const handleExtendDialogClose = () => {
      setShowExtendConfirm(false);
      setExtendErrorMessage(null);
      setIsDropdownOpen(false);
      setIsHovered(false);
    };

    const confirmShorten = async (newEndDate: Date) => {
      if (!shouldShowActionMenu) {
        setShowShortenConfirm(false);
        setShortenErrorMessage(null);
        return;
      }

      setActiveShortenTripId(trip.id);
      setShortenErrorMessage(null);
      try {
        const formattedDate = format(newEndDate, 'yyyy-MM-dd');
        const newTrip = await shortenTripMutation.mutateAsync({
          tripId: trip.id,
          endDate: formattedDate,
        });
        showToast({
          message: (tToasts('tripShortened') as string) ?? t('actions.shrink'),
          variant: 'success',
        });
        if (newTrip?.id) {
          router.push(`/trips/${newTrip.id}/edit?derived=shorten`);
        }
        setShowShortenConfirm(false);
        setActiveShortenTripId(null);
        setIsDropdownOpen(false);
        setIsHovered(false);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('errorLoading') || 'Failed to shorten trip';
        setShortenErrorMessage(message);
        setActiveShortenTripId(null);
      }
    };

    const handleShortenDialogClose = () => {
      setShowShortenConfirm(false);
      setShortenErrorMessage(null);
      setIsDropdownOpen(false);
      setIsHovered(false);
    };

    const handleRowClick = (e: React.MouseEvent) => {
      // Don't navigate if clicking on buttons or dropdown elements
      const target = e.target as HTMLElement;

      // Check if clicking on any interactive element
      if (
        target.closest('button') ||
        target.closest('[role="menuitem"]') ||
        target.closest('[role="menu"]') ||
        target.closest('[data-radix-collection-item]') ||
        target.closest('[data-state]') // Radix UI elements often have data-state
      ) {
        return;
      }

      // Don't navigate if dropdown is currently open or closing
      if (isDropdownOpen || dropdownClosingRef.current) {
        return;
      }

      // Pass state to indicate where we came from
      const navigationState = variant === 'approvals' ? { from: '/approvals' } : undefined;
      router.push(`/trips/${trip.id}`, { state: navigationState });
    };

    return (
      <>
        <TableRow
          className="cursor-pointer border-b border-[#f0f0f0] transition-colors duration-75 hover:bg-gray-50"
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onClick={handleRowClick}
          style={{ touchAction: 'none' }}
        >
          <TableCell className={`h-[4.25rem] text-left ${columnClassMap.initiator ?? ''}`}>
            <UserCell user={trip.initiator} />
          </TableCell>
          <TableCell className={`h-[4.25rem] text-left ${columnClassMap.author ?? ''}`}>
            <UserCell user={trip.author} />
          </TableCell>
          <TableCell
            className={`h-[4.25rem] truncate text-left text-[0.875rem] text-[#666] ${
              columnClassMap.number ?? ''
            }`}
            title={trip.number}
          >
            {trip.number}
          </TableCell>
          <TableCell
            className={`h-[4.25rem] truncate text-left text-[0.875rem] text-[#666] ${
              columnClassMap.duration ?? ''
            }`}
            title={formatDateRange(trip.startDate, trip.endDate)}
          >
            {formatDateRange(trip.startDate, trip.endDate)}
          </TableCell>
          <TableCell
            className={`h-[4.25rem] truncate text-left text-[0.875rem] text-[#666] ${
              columnClassMap.submittedAt ?? ''
            }`}
            title={submittedDate === DATE_PLACEHOLDER ? undefined : submittedDate}
          >
            {submittedDate}
          </TableCell>
          {hasMyStatusColumn ? (
            <TableCell className={`h-[4.25rem] text-left ${columnClassMap.myStatus ?? ''}`}>
              <div
                className={`transition-opacity duration-200 ${
                  shouldShowApprovalControls && (isHovered || isActionPending)
                    ? 'pointer-events-none opacity-0'
                    : 'opacity-100'
                }`}
              >
                {myStateBadge ?? (
                  <span className="text-sm leading-5 text-[#666666]">{DATE_PLACEHOLDER}</span>
                )}
              </div>
            </TableCell>
          ) : null}
          <TableCell className={`relative h-[4.25rem] text-left ${columnClassMap.status ?? ''}`}>
            {/* Show status badge unless row has actions and is hovered/open/updating */}
            <div
              className={`transition-opacity duration-200 ${
                hasRowActions && (isHovered || isDropdownOpen || isActionPending)
                  ? 'opacity-0'
                  : 'opacity-100'
              }`}
            >
              {getStatusBadge(trip.status, t, statusLabelOverride)}
            </div>

            {/* Show approve/deny buttons for "pending" status, and keep them visible while updating */}
            {shouldShowApprovalControls ? (
              <>
                {/* Approve/Deny buttons container */}
                <div
                  className={`absolute inset-0 flex items-center justify-end gap-3.5 pr-16 transition-opacity duration-200 ${
                    isHovered || isActionPending ? 'opacity-100' : 'opacity-0'
                  } ${!isHovered && !isActionPending ? 'pointer-events-none' : 'pointer-events-auto'}`}
                >
                  {/* Reject button */}
                  {canDecline && (
                    <Button
                      onClick={handleReject}
                      disabled={isActionPending}
                      variant="unstyled"
                      size="none"
                      className="relative z-10 inline-flex cursor-pointer items-center gap-1 rounded-[200px] bg-[rgba(255,112,112,0.08)] py-1.5 pr-3 pl-2 text-[14px] leading-[20px] font-medium text-[#e5484d] transition-colors hover:bg-[rgba(255,112,112,0.12)]"
                    >
                      {isDecliningThisTrip ? (
                        <Spinner variant="circle" size={16} className="text-[#e5484d]" />
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 4L4 12M4 4L12 12"
                            stroke="#E5484D"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      <span>{t('actions.reject')}</span>
                    </Button>
                  )}

                  {/* Approve button */}
                  <Button
                    onClick={handleApprove}
                    disabled={isActionPending}
                    variant="unstyled"
                    size="none"
                    className="relative z-10 inline-flex cursor-pointer items-center gap-1 rounded-[200px] bg-[#242424] py-1.5 pr-3 pl-2 text-[14px] leading-[20px] font-medium text-white transition-colors hover:bg-[#1a1a1a]"
                  >
                    {isApprovingThisTrip ? (
                      <Spinner variant="circle" size={16} className="text-white" />
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.6667 5L7.50004 14.1667L3.33337 10"
                          stroke="#FFFFFF"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    <span>{t('actions.approve')}</span>
                  </Button>
                </div>

                {/* Three dots button - separate container to maintain position */}
                {shouldShowActionMenu && dropdownActions.length > 0 ? (
                  <div
                    className={`absolute inset-0 flex items-center justify-end pr-4 transition-opacity duration-200 ${
                      isHovered || isDropdownOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionDropdown
                      actions={dropdownActions}
                      onOpenChange={handleDropdownOpenChange}
                    />
                  </div>
                ) : null}
              </>
            ) : shouldShowActionMenu && dropdownActions.length > 0 ? (
              <div
                className={`absolute inset-0 flex items-center justify-end pr-4 transition-opacity duration-200 ${
                  isHovered || isDropdownOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <ActionDropdown actions={dropdownActions} onOpenChange={handleDropdownOpenChange} />
              </div>
            ) : null}
          </TableCell>
        </TableRow>

        {/* Approval Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showApproveConfirm}
          onClose={() => setShowApproveConfirm(false)}
          onConfirm={confirmApprove}
          title={t('approveConfirmation.title')}
          message={t('approveConfirmation.message')}
          confirmText={t('approveConfirmation.confirm')}
          cancelText={t('approveConfirmation.cancel')}
          confirmButtonVariant="brand"
          isLoading={isApprovingThisTrip}
        />

        {/* Rejection Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showRejectConfirm}
          onClose={() => setShowRejectConfirm(false)}
          onConfirm={confirmReject}
          title={t('rejectConfirmation.title')}
          message={t('rejectConfirmation.message')}
          confirmText={t('rejectConfirmation.confirm')}
          cancelText={t('rejectConfirmation.cancel')}
          confirmButtonVariant="danger"
          isLoading={isDecliningThisTrip}
        />

        {/* Cancel Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={confirmCancel}
          title={t('cancelConfirmation.title')}
          message={t('cancelConfirmation.message')}
          confirmText={t('cancelConfirmation.confirm')}
          cancelText={t('cancelConfirmation.cancel')}
          confirmButtonVariant="danger"
          isLoading={isCancellingThisTrip}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          title={t('deleteConfirmation.title')}
          message={t('deleteConfirmation.message')}
          confirmText={t('deleteConfirmation.confirm')}
          cancelText={t('deleteConfirmation.cancel')}
          confirmButtonVariant="danger"
          isLoading={isDeletingThisTrip}
        />

        {/* Extend Confirmation Dialog */}
        <TripExtendDialog
          isOpen={showExtendConfirm}
          onClose={handleExtendDialogClose}
          onConfirm={confirmExtend}
          currentEndDate={trip.endDate}
          isLoading={isExtendingThisTrip}
          errorMessage={extendErrorMessage ?? undefined}
        />

        {/* Shorten Confirmation Dialog */}
        <TripShortenDialog
          isOpen={showShortenConfirm}
          onClose={handleShortenDialogClose}
          onConfirm={confirmShorten}
          currentStartDate={trip.startDate}
          currentEndDate={trip.endDate}
          isLoading={isShorteningThisTrip}
          errorMessage={shortenErrorMessage ?? undefined}
        />
      </>
    );
  }
);

TripRow.displayName = 'TripRow';

export default function TripsTable({
  filters = {},
  search = '',
  useTripsHook = useTrips,
  variant = 'default',
  currentUserRoles = [],
}: TripsTableProps) {
  const t = useTranslations('trips');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [sortBy, setSortBy] = useState<TripSortField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const filterSignatureRef = useRef<{ filters: string; search: string }>({
    filters: '',
    search: '',
  });

  const columns = variant === 'approvals' ? APPROVAL_TABLE_COLUMNS : BASE_TABLE_COLUMNS;
  const filtersMap = filters ?? EMPTY_FILTERS;
  const normalizedFilters = useMemo(() => normalizeFilters(filtersMap), [filtersMap]);
  const startDateFrom = normalizedFilters.start_date_from?.[0];
  const startDateTo = normalizedFilters.start_date_to?.[0];
  const endDateFrom = normalizedFilters.end_date_from?.[0];
  const endDateTo = normalizedFilters.end_date_to?.[0];
  const normalizedSearch = (search ?? '').trim();
  const selectedTripTypes = useMemo(() => {
    const values = normalizedFilters.trip_type ?? [];
    const unique: Array<'local' | 'international'> = [];

    values.forEach((value) => {
      if ((value === 'local' || value === 'international') && !unique.includes(value)) {
        unique.push(value);
      }
    });

    return unique;
  }, [normalizedFilters]);
  const selectedStates = useMemo(() => {
    const values = normalizedFilters.status ?? [];
    const unique: string[] = [];

    values.forEach((value) => {
      if (typeof value === 'string' && value.length > 0 && !unique.includes(value)) {
        unique.push(value);
      }
    });

    return unique;
  }, [normalizedFilters]);

  useEffect(() => {
    const filtersSignature = JSON.stringify(normalizedFilters);
    const previous = filterSignatureRef.current;

    if (previous.filters !== filtersSignature || previous.search !== normalizedSearch) {
      filterSignatureRef.current = { filters: filtersSignature, search: normalizedSearch };
      setPage(DEFAULT_PAGE);
    }
  }, [normalizedFilters, normalizedSearch]);

  const tripQueryOptions = useMemo<TripListOptions>(() => {
    const options: TripListOptions = {
      page,
      pageSize: PAGE_SIZE,
    };

    if (selectedTripTypes.length > 0) {
      options.tripTypes = selectedTripTypes;
    }

    if (selectedStates.length > 0) {
      options.states = selectedStates;
    }

    if (startDateFrom) {
      options.startDateFrom = startDateFrom;
    }
    if (startDateTo) {
      options.startDateTo = startDateTo;
    }
    if (endDateFrom) {
      options.endDateFrom = endDateFrom;
    }
    if (endDateTo) {
      options.endDateTo = endDateTo;
    }

    if (normalizedSearch) {
      options.search = normalizedSearch;
    }

    if (sortBy) {
      options.sortBy = sortBy;
      options.sortOrder = sortOrder;
    }

    return options;
  }, [
    page,
    selectedTripTypes,
    selectedStates,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    normalizedSearch,
    sortBy,
    sortOrder,
  ]);

  const { data, isLoading, isFetching, error } = useTripsHook(tripQueryOptions);

  useEffect(() => {
    if (data && data.page && data.page !== page) {
      setPage(data.page);
    }
  }, [data, page]);

  const trips = data?.trips ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const currentPage = data?.page ?? page;
  const pageSizeFromApi = data?.pageSize ?? PAGE_SIZE;
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSizeFromApi + 1;
  const endRecord =
    totalRecords === 0 ? 0 : Math.min(startRecord + pageSizeFromApi - 1, totalRecords);
  const pageNumbers = useMemo(
    () => getPageNumbers(currentPage, totalPages, MAX_PAGE_BUTTONS),
    [currentPage, totalPages]
  );
  const showSpinner = isFetching && !isLoading;

  const handleSortChange = (field?: TripSortField) => {
    if (!SORTING_ENABLED || !field) {
      return;
    }

    if (sortBy === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        setSortBy(undefined);
        setSortOrder('asc');
      }
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }

    setPage(DEFAULT_PAGE);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) {
      return;
    }

    setPage(nextPage);
  };
  const viewState: TripListViewState = {
    data,
    trips,
    totalRecords,
    totalPages,
    currentPage,
    pageSize: pageSizeFromApi,
    startRecord,
    endRecord,
    pageNumbers,
    showSpinner,
    isLoading,
    isFetching,
    error: error ?? null,
    handlePageChange,
  };

  return (
    <div className="w-full bg-white">
      <div className="hidden lg:block">
        <DesktopTableView
          columns={columns}
          t={t}
          sortBy={sortBy}
          sortOrder={sortOrder}
          handleSortChange={handleSortChange}
          variant={variant}
          currentUserRoles={currentUserRoles}
          viewState={viewState}
        />
      </div>
      <div className="lg:hidden">
        <TripsListMobile
          variant={variant}
          currentUserRoles={currentUserRoles}
          viewState={viewState}
        />
      </div>
    </div>
  );
}
